import React, { useState } from "react";
import { AiOutlineClockCircle, AiOutlineQuestionCircle } from "react-icons/ai";
import { MdDelete } from "react-icons/md";
import { useGetAllAssignmentQuestionQuery } from "../../../graphql/generated.tsx";

const ACCENTS = [
  { bar: "#8A5A2B", pillBg: "#F3E8DA", pillText: "#8A5A2B", pillBgHover: "#EAD9C3" },
  { bar: "#0F6E56", pillBg: "#E1F5EE", pillText: "#0F6E56", pillBgHover: "#C9EADF" },
  { bar: "#3C3489", pillBg: "#EEEDFE", pillText: "#3C3489", pillBgHover: "#E0DFFE" },
];

const formatDuration = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const raw = String(value).trim();
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return "-";

  const [hoursPart, minutesPart] = raw.split(".");
  const hours = Number.parseInt(hoursPart, 10) || 0;
  const minutes = Number.parseInt(minutesPart, 10);
  if (minutesPart?.length === 2 && Number.isFinite(minutes) && minutes <= 59) {
    return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
  }

  const totalMinutes = Math.round(numeric * 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  return remainingMinutes ? `${totalHours} hr ${remainingMinutes} min` : `${totalHours} hr`;
};

const Arrow = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Calendar = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.5" y="2.5" width="10" height="9" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 1.5v2M8.5 1.5v2M1.5 5.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const AssignmentCard = ({
  assignment_id,
  name,
  description,
  due_date,
  duration,
  domain,
  globalIndex = 0,
  onRemove,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const accent = ACCENTS[globalIndex % ACCENTS.length];
  const { data: questionsData } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId: assignment_id || "", page: 1, limit: 1000 },
    skip: !assignment_id,
  });
  const questionsCount = questionsData?.getAllAssignmentQuestion?.length ?? "-";

  return (
    <div
      className="relative"
      style={{ flex: "1 1 380px", minWidth: 320, maxWidth: 560 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ minHeight: 256, background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 12px 30px -18px rgba(15, 23, 42, 0.2)", fontFamily: "Poppins, sans-serif" }}>
        <div style={{ height: 7, background: `${accent.bar}99`, flexShrink: 0 }} />
        <div style={{ padding: "20px 18px 16px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <p style={{ margin: 0, minWidth: 0, flex: 1, fontSize: 16, fontWeight: 600, color: "#101828", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{name}</p>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(assignment_id)}
                aria-label="Delete assignment from cluster"
                title="Delete assignment from cluster"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-red-600 shadow-sm transition-opacity hover:bg-red-50"
                style={{ opacity: isHovered ? 1 : 0, pointerEvents: isHovered ? "auto" : "none" }}
              >
                <MdDelete size={20} />
              </button>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 14, color: "#5B6475", lineHeight: 1.7, flex: 1, fontWeight: 500, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{description || "Take this assessment based on what you know and demonstrate your understanding."}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "4px 0", marginTop: 6 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#0F172A" }}><AiOutlineClockCircle size={16} color="#64748B" />{formatDuration(duration)}</span>
            <span style={{ width: 1, height: 16, background: "#E2E8F0" }} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#0F172A" }}><AiOutlineQuestionCircle size={16} color="#64748B" />{questionsCount} questions</span>
            <span style={{ width: 1, height: 16, background: "#E2E8F0" }} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#0F172A" }}><Calendar />{due_date ? new Date(due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "No due date"}</span>
          </div>
        </div>
        <div style={{ padding: "12px 18px 16px", display: "flex", alignItems: "center", borderTop: "1px solid #F2F4F7", gap: 10, background: "linear-gradient(90deg, rgba(248,250,252,0.9), rgba(255,255,255,0.6))" }}>
          {domain ? <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 9px", borderRadius: 8, letterSpacing: "0.03em", whiteSpace: "nowrap", background: accent.pillBg, color: accent.pillText }}>{domain.toUpperCase()}</span> : <span />}
          <span style={{ width: 26, height: 26, borderRadius: "50%", background: isHovered ? accent.pillBgHover : "#F9FAFB", border: `1px solid ${isHovered ? accent.bar : "#EAECF0"}`, color: isHovered ? accent.bar : "#667085", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto" }}><Arrow /></span>
        </div>
      </div>
    </div>
  );
};

export default AssignmentCard;
